import React, { useCallback, useMemo } from "react";
import { CBadge, CButton, CTooltip } from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import { useBacktests, useExecutions } from "@src/hooks/redux";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import { formatDateAsLocale, formatDateTimeShort } from "@src/utils/dateUtils";
import { CDataTable } from "@tradingdroid/frontends-components";
import type { CDataTableColumn } from "@tradingdroid/frontends-components";
import { Link } from "react-router-dom";

interface Props {
  type: "backtest" | "execution";
  runs: T.ApiRun[];
  loading?: boolean;
}

export const RunListTable: React.FC<Props> = ({ type, runs, loading = false }) => {
  const { showJsonModal } = useJsonModal();
  const { confirmModal } = useConfirmModal();
  const { showToast } = useToastActions();

  const { removeBacktest } = useBacktests();
  const { removeExecution } = useExecutions();

  const handleDelete = useCallback(
    async (run: T.ApiRun) => {
      const entityName = type === "backtest" ? "backtest" : "execution";
      const { confirmed } = await confirmModal(
        `Are you sure you want to delete this ${entityName}?`,
        {
          title: `Delete ${entityName}`,
        },
      );
      if (!confirmed) return;

      try {
        if (type === "backtest") {
          await tdApi.deleteBacktest(run.id);
          removeBacktest(run.id);
        } else {
          await tdApi.deleteExecution(run.id);
          removeExecution(run.id);
        }
        showToast({ message: `${entityName} deleted successfully`, type: "success" });
      } catch (error) {
        console.error(`Failed to delete ${entityName}:`, error);
        showToast({ message: `Failed to delete ${entityName}`, type: "danger" });
      }
    },
    [type, confirmModal, removeBacktest, removeExecution, showToast],
  );

  // Define table columns (works for both types)
  const tableColumns = useMemo<CDataTableColumn<T.ApiRun>[]>(
    () => [
      {
        columnKey: "id",
        columnLabel: "ID",
        columnStyle: { width: "70px" },
        cellRender: (run: T.ApiRun) => (
          <>
            <div>
              <Link
                className="td-idLink"
                to={type === "backtest" ? `/backtest/${run.id}` : `/execution/${run.id}`}
              >
                {run.id}
              </Link>
              <CButton
                color="info"
                variant="ghost"
                size="sm"
                className="ms-1 p-0"
                onClick={() => showJsonModal(run, `Full ${type}: "${run.name}"`)}
                title="View full run data"
              >
                ℹ️
              </CButton>
            </div>
            {run.tags && run.tags.length > 0 && (
              <CTooltip
                content={run.tags.map((t, i) => (
                  <React.Fragment key={t}>
                    {i > 0 && <br />}
                    {t}
                  </React.Fragment>
                ))}
              >
                <CBadge color="secondary" size="sm" className="RunListTable-tagsBadge">
                  {run.tags.length} tag{run.tags.length > 1 ? "s" : ""}
                </CBadge>
              </CTooltip>
            )}
          </>
        ),
      },
      {
        columnKey: "name",
        columnLabel: "Name",
        columnStyle: { width: "auto" },
        cellRender: (run: T.ApiRun) => run.name,
      },
      {
        columnKey: "job_id",
        columnLabel: "Job",
        columnStyle: { width: "70px" },
        cellRender: (run: T.ApiRun) => {
          if (!run.job_id) return "-";

          return (
            <>
              {run.job_id}
              <CButton
                color="info"
                variant="ghost"
                size="sm"
                className="ms-1 p-0"
                onClick={async () => {
                  try {
                    const job = await tdApi.getJob(run.job_id!);
                    if (job) {
                      showJsonModal(job, `Job #${run.job_id}`);
                    } else {
                      showJsonModal({ error: `Job #${run.job_id} not found` }, `Error`);
                    }
                  } catch (err) {
                    console.error("Failed to load job", err);
                    showJsonModal({ error: `Failed to load Job #${run.job_id}` }, `Error`);
                  }
                }}
                title="View job data"
              >
                ℹ️
              </CButton>
            </>
          );
        },
      },
      {
        columnKey: "trades",
        columnLabel: "Trades",
        columnStyle: { width: "60px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const trades = run.metrics?.total_positions;
          if (trades === null || trades === undefined) return "-";
          return <ColoredValue value={trades} type="flat" />;
        },
      },
      {
        columnKey: "wins",
        columnLabel: "Wins",
        columnStyle: { width: "60px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const wins = run.metrics?.win_signals;
          if (wins === null || wins === undefined) return "-";
          return <ColoredValue value={wins} type="flat" forceColor="win" />;
        },
      },
      {
        columnKey: "losses",
        columnLabel: "Losses",
        columnStyle: { width: "60px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const losses = run.metrics?.loss_signals;
          if (losses === null || losses === undefined) return "-";
          return <ColoredValue value={losses} type="flat" forceColor="loss" />;
        },
      },
      {
        columnKey: "win_rate",
        columnLabel: "Win Rate",
        columnStyle: { width: "90px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const win_rate = run.metrics?.win_rate;
          if (win_rate === null || win_rate === undefined) return "-";
          return (
            <ColoredValue value={win_rate * 100} type="percentage" threshold={50} decimals={1} />
          );
        },
      },
      {
        columnKey: "profit",
        columnLabel: "Profit",
        columnStyle: { width: "100px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const cumulative_returns = run.metrics?.cumulative_returns;
          if (cumulative_returns === null || cumulative_returns === undefined) return "-";
          return <ColoredValue value={cumulative_returns} type="currency" threshold={0} />;
        },
      },
      {
        columnKey: "pnl_pct",
        columnLabel: "PnL %",
        columnStyle: { width: "80px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const cumulative_returns_pct = run.metrics?.cumulative_returns_pct;
          if (cumulative_returns_pct === null || cumulative_returns_pct === undefined) return "-";
          return <ColoredValue value={cumulative_returns_pct} type="percentage" threshold={0} />;
        },
      },
      {
        columnKey: "apy",
        columnLabel: "APY",
        columnStyle: { width: "80px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          const annualized_return_pct = run.metrics?.annualized_return_pct;
          if (annualized_return_pct === null || annualized_return_pct === undefined) return "-";
          return <ColoredValue value={annualized_return_pct} type="percentage" threshold={0} />;
        },
      },
      {
        columnKey: "start",
        columnLabel: "Start",
        columnStyle: { width: "140px" },
        cellRender: (run: T.ApiRun) => (run.start_ts ? formatDateTimeShort(run.start_ts) : "-"),
      },
      {
        columnKey: "end",
        columnLabel: "End",
        columnStyle: { width: "100px" },
        cellRender: (run: T.ApiRun) => (run.end_ts ? formatDateAsLocale(run.end_ts) : "-"),
      },
      {
        columnKey: "actions",
        columnLabel: "Actions",
        columnStyle: { width: "65px", textAlign: "right" as const },
        cellRender: (run: T.ApiRun) => {
          // Disable delete for running executions (no end_ts)
          // They should be ended first via command sent
          const isRunningExecution = type === "execution" && !run.end_ts;

          return (
            <CButton
              color="danger"
              onClick={() => handleDelete(run)}
              size="sm"
              disabled={isRunningExecution}
              title={isRunningExecution ? "End the execution first before deleting" : "Delete"}
            >
              🗑
            </CButton>
          );
        },
      },
    ],
    [type, handleDelete, showJsonModal],
  );

  return (
    <CDataTable
      className={`RunListTable type-${type}`}
      columns={tableColumns}
      items={runs}
      striped
      small
      hover
      responsive
      loading={loading}
      getRowProps={(run) => ({ id: `${type}-${run.id}` })}
      getCellProps={(run, column) => ({ id: `${type}-${run.id}-${column.columnKey}` })}
    />
  );
};
